import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, Ticket, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase, supabaseConfigured } from "@/lib/supabase";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!supabaseConfigured) return toast.error("Supabase niet geconfigureerd");
    setSubmitting(true);
    try {
      const redirectTo = `${window.location.origin}/admin/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo },
      );
      if (error) throw error;
      setSent(true);
      toast.success("E-mail verzonden");
    } catch (err) {
      toast.error(err?.message || "Kon reset-mail niet versturen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-forgot-password-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
            <Ticket className="h-5 w-5 text-white" />
          </span>
          <span className="text-white font-extrabold tracking-tight text-xl">
            StudioVoorJou<span className="text-svj-cyan"> Beheer</span>
          </span>
        </Link>

        <div className="bg-svj-card border border-svj-border rounded-2xl p-8">
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
            Wachtwoord vergeten
          </div>
          <h1 className="mt-1 text-2xl font-extrabold text-white">Reset-link aanvragen</h1>
          <p className="mt-2 text-sm text-slate-400">
            Vul je e-mailadres in — je krijgt een link waarmee je een nieuw wachtwoord kunt instellen.
          </p>

          {sent ? (
            <div data-testid="forgot-password-sent" className="mt-8 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 text-sm text-emerald-200">
              <div className="flex items-center gap-2 font-semibold text-emerald-100">
                <Mail className="h-4 w-4" /> Check je inbox
              </div>
              <p className="mt-2 text-emerald-100/80">
                Als er een account bij <b>{email}</b> hoort, ontvang je binnen enkele minuten een reset-link.
                De link is 60 minuten geldig.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <label className="block" htmlFor="forgot-email">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  E-mailadres
                </span>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email"
                  className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="forgot-password-submit"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                  submitting
                    ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                }`}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                {submitting ? "Bezig…" : "Stuur reset-link"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/admin/login" data-testid="forgot-password-back" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> Terug naar inloggen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
